import { Loading } from "@components/Loading";
import { SearchableSelect } from "@components/SearchableSelect";
import { useTranslation } from "@helpers/useTranslation";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { FirebaseContext } from "src/helpers/firebase";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useHasNoPin } from "src/helpers/useHasNoPin";
import { usePersistedState } from "src/helpers/usePersistedState";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useToast } from "src/helpers/useToast";

// Above this many teams a plain <select> becomes an unusable scroll; switch to a search input.
const SEARCHABLE_TEAMS_THRESHOLD = 20;

export const SelectTeam = () => {
  const { t } = useTranslation("selectteam");

  const { pinAuth, functions } = useContext(FirebaseContext);
  const [teamSelected, setTeamSelected] = useState<Team | undefined>();
  const [appData, setAppData] = usePersistedState();
  const eventId = useEventId();
  const [event, loadingEvent, errorEvent] = useEvent();

  const [password, setPassword] = useState("");
  const needsPassword = !!teamSelected?.password;
  const passwordMismatch = teamSelected?.password !== password;

  const toast = useToast();
  const [teams] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const selectableTeams = useMemo(() => teams?.filter((team) => !team?.isUploader), [teams]);
  const sortedTeams = useMemo(() => {
    // ── URBOSTOGOS (event-specific, throwaway) ───────────────────────────
    // Customer requested a fixed, non-alphabetical team order. Team IDs are
    // fixed, so we order by them. Remove when this event is over.
    if (eventId === "urbostogos") {
      const urboStogosOrder = [
        "32a10036fef8fec7bf1b84fbcd1506eead3e5c87464d44559ddbdb58bc45d7d6", // Balis
        "4f561349996532e54768b396b048083b9f388c57cc24ae3be0dd468275afba8a", // Tulumas
        "9674e6e7c27c9a41a5a090105dc04303894b3a04417e47d6726bd081912a3faa", // Barselona
        "dbfe096fdbca4a7c59de710e5c0fcddd94d52c8fb82a4c82c78ffd2a56e26da1", // Maljorka
        "219b6c7193d6e19b75564a0ce8239651e4630bf34ceee8d3706c1c7622ee8f9e", // Ibiza
        "8bcc7b1fba4e690e02e6b2cdddd8db97cb3bd0c5f8f0a47562b374ada8cec724", // Havajai
      ];
      const rank = (id: string) => {
        const i = urboStogosOrder.indexOf(id);
        return i === -1 ? urboStogosOrder.length : i; // unknown teams sink to the end
      };
      return selectableTeams?.slice().sort((a, b) => rank(a.id) - rank(b.id));
    }
    // ─────────────────────────────────────────────────────────────────────

    return selectableTeams?.slice().sort((a, b) => {
      if (!a.name || !b.name) return 0;
      return a.name.localeCompare(b.name);
    });
  }, [selectableTeams, eventId]);

  useEffect(() => {
    if (errorEvent) console.error(errorEvent);
  }, [errorEvent]);

  const navigate = useEventNavigate();
  const [exchangePinForToken, _, errorExchangePin] = useHttpsCallable(functions, "exchangePinForTokenV2");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (errorExchangePin) {
      if (errorExchangePin.message.includes("Invalid PIN code")) {
        toast(t("invalidPinCode"), "🤷‍♂️", "error");
      } else {
        toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      }
      console.error(errorExchangePin);
    }
  }, [errorExchangePin]);

  const handleTeamSelect = async () => {
    try {
      setLoading(true);
      const pin = appData?.pin;
      const result = await exchangePinForToken({ pin, eventId, teamName: teamSelected.name.trim(), password });
      const token = result?.data as string;
      const res = await pinAuth(token);
      const uid = res.user?.uid;
      if (uid) {
        const deeplink = appData?.deeplink;
        setAppData({ ...appData, uid, isUploader: false, deeplink: undefined });
        await new Promise((resolve) => setTimeout(resolve, 200));
        navigate(deeplink ?? "/challenges");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useHasNoPin();

  const toSearchableString = (str: string) => str.trim().toLowerCase().replace(/\s/g, "");

  const isPlayers = event?.mainConfig?.playersOnly ?? false;
  const title = isPlayers ? t("titlePlayers") : t("title");
  const about = isPlayers ? t("aboutPlayers") : t("about");
  const selectTeam = isPlayers ? t("selectPlayer") : t("selectTeam");
  const pickExisting = isPlayers ? t("pickExistingPlayer") : t("pickExistingTeam");
  const passwordCopy = isPlayers ? t("playerPassword") : t("teamPassword");
  const searchCopy = isPlayers ? t("searchPlayer") : t("searchTeam");
  const noResultsCopy = isPlayers ? t("noPlayersFound") : t("noTeamsFound");

  const isSearchable = (sortedTeams?.length ?? 0) >= SEARCHABLE_TEAMS_THRESHOLD;

  return (
    <main className="flex-1 flex-col">
      <Loading loading={!event || loadingEvent}>
        <div className="flex flex-col px-6 py-4">
          <div>
            <h1 className="mt-12 mb-8 text-center text-4xl font-semibold">{title}</h1>
            <div className="mt-16 mb-8 text-center text-lg font-normal">{about}</div>
          </div>
          <div className="mx-6 flex justify-center">
            <div className="form-control w-full max-w-xs">
              <label className="label">
                <span className="label-text">{pickExisting}</span>
              </label>
              {isSearchable ? (
                <SearchableSelect
                  options={sortedTeams.map(({ id, name, displayName }) => ({ id, label: displayName ?? name }))}
                  placeholder={searchCopy}
                  noResultsCopy={noResultsCopy}
                  onSelect={(id) => setTeamSelected(selectableTeams.find((team) => team.id === id))}
                />
              ) : (
                <select
                  className="select-bordered select bg-gray-200"
                  onChange={(e) => {
                    const teamName = e.target.value;
                    const teamSelected = selectableTeams.find(
                      (team) => toSearchableString(team.displayName ?? team.name) === toSearchableString(teamName),
                    );
                    setTeamSelected(teamSelected);
                  }}
                >
                  <option disabled selected>
                    {selectTeam}
                  </option>
                  {sortedTeams.map(({ id, name, displayName }) => (
                    <option key={id}>{displayName ?? name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {needsPassword && (
            <div className="mx-6 mt-4 flex justify-center">
              <div className="form-control w-full max-w-xs">
                <label className="label">
                  <span className="label-text">{passwordCopy}</span>
                </label>
                <input
                  className="input-bordered input"
                  type="password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                />
              </div>
            </div>
          )}

          <div className="mt-12 flex justify-center">
            <button
              className={`btn-primary btn mb-4 px-4 ${loading ? "loading" : ""}`}
              disabled={!teamSelected || loading || (needsPassword && passwordMismatch)}
              onClick={handleTeamSelect}
            >
              {t("play")}
            </button>
          </div>
        </div>
      </Loading>
    </main>
  );
};
